/**
 * Authorisation model.
 *
 * DESIGN
 * Two independent axes, both of which must allow an action:
 *
 *   1. ROLE  — what a user may do *inside* an organisation they belong to.
 *              Expressed as a capability matrix below.
 *   2. TIER  — what an organisation may do *beyond itself* (manage other
 *              organisations, mint credits, edit framework content).
 *
 * A user acting in organisation X has: the capabilities of their role in X,
 * intersected with what X's tier permits. Being an ADMINISTRATOR of a CLIENT
 * organisation does not let you create other organisations, because the CLIENT
 * tier has no such power — and being a DISTRIBUTOR does not let an EMPLOYEE
 * mint credits, because the EMPLOYEE role has no such capability.
 *
 * FAIL-CLOSED
 * `can()` returns false for any capability not explicitly granted. Routes that
 * forget to declare a capability are rejected by the route guard rather than
 * defaulting to open — see api/src/plugins/authorize.ts.
 *
 * This module is shared with the front end so the UI can hide what the user
 * cannot do. That is a usability affordance ONLY. The server re-checks every
 * capability on every request; nothing here is a client-side security control.
 */
/**
 * Every action the system can perform, named `resource.action`.
 * Keep this list exhaustive: a capability that is not listed cannot be granted.
 */
export const CAPABILITIES = [
    // Organisation
    'org.read',
    'org.update',
    'org.delete',
    'org.create',
    'org.consent.manage', // grant/withdraw consent to upstream management
    'org.join', // join a consenting downstream organisation
    'org.group.manage',
    // Users
    'user.read',
    'user.create',
    'user.update',
    'user.delete',
    'user.credentials.reset', // trigger password reset / MFA reset for others
    // Frameworks
    'framework.read',
    'framework.purchase',
    'framework.manage', // edit the question bank itself (platform-level)
    // Assessments
    'submission.read',
    'submission.answer',
    'submission.finalize',
    /**
     * Reopen a FINALIZED submission for editing.
     *
     * This is a NEW capability rather than a reuse of `submission.finalize`, and
     * the distinction is deliberate. Reopening breaks the one-way-door invariant
     * that the whole assessment module is built around — it is at least as
     * consequential as finalising, arguably more so, because it unlocks a record
     * an auditor may already be relying on and can invalidate the gaps that
     * remediation plans were built from. Folding it into `submission.finalize`
     * would silently grant reopen power to every finaliser with no way to revoke
     * it separately, and no capability already in this list means "may break a
     * seal". Splitting it keeps the grant explicit and separately auditable, and
     * lets an organisation give finalise to many while keeping reopen to few.
     */
    'submission.reopen',
    'evidence.upload',
    'evidence.delete',
    'control.evaluate',
    'control.custom.manage',
    // Gaps & remediation
    'gap.read',
    'gap.update',
    'plan.read',
    'plan.readOwn', // EMPLOYEE: only plans they own
    'plan.create',
    'plan.update',
    'plan.updateOwn',
    'plan.delete',
    // Policies
    'policy.read',
    'policy.create',
    'policy.update',
    'policy.delete',
    'policy.approve',
    'template.manage',
    /**
     * Information-security risk register (ISO 27001 clause 6.1 / NIS2 Art 21 /
     * DORA ICT risk management).
     *
     * WHY THESE ARE NEW RATHER THAN BORROWED
     * The nearest existing capabilities are all the wrong record:
     *   `gap.*`    — a gap is a traceable assessment shortfall with an originating
     *                answer. A risk is a forward judgement that may have no gap
     *                behind it at all; giving it a gap capability would be the
     *                first step towards the two registers bleeding into each other.
     *   `vendor.*` — that is the SUPPLIER risk score (risk.ts), a different scale
     *                and a different artefact. Reusing it would mean a role change
     *                aimed at third-party management silently moved who may edit
     *                the enterprise risk register.
     *   `org.update` — far too broad; it also grants organisation settings.
     *
     * Split read from manage for the same reason the rest of the matrix is: an
     * auditor and an executive must READ the register (it is core audit evidence)
     * without being able to change a score or accept a risk.
     */
    'risk.read',
    'risk.manage',
    // Third-party risk
    'vendor.read',
    'vendor.create',
    'vendor.update',
    'vendor.delete',
    'vendor.assess', // send an assessment (spends credits)
    // Scanning
    'scan.read',
    'scan.schedule', // spends credits
    // Maritime
    'maritime.read',
    'maritime.create',
    'maritime.update',
    'maritime.delete',
    /**
     * NIS2 Article 23 incident reporting.
     *
     * WHY THESE ARE NEW RATHER THAN BORROWED
     * Nothing in this list already means "may read this organisation's live
     * incident records". The near misses are all wrong in a way that matters:
     *
     *   `gap.*`        — an incident is not a gap. It is not generated by
     *                    finalising an assessment, it does not belong in the gap
     *                    register, and giving it a gap capability would be the
     *                    first step towards it leaking into one.
     *   `submission.*` — those gate a self-assessment the customer fills in at
     *                    leisure. `submission.answer` is held by exactly the
     *                    right people, but reusing it would mean a role change
     *                    aimed at assessments silently changed who may declare a
     *                    live incident.
     *   `scan.*`       — about this product scanning the customer, not about the
     *                    customer's own incident records.
     *
     * WHY THREE AND NOT ONE
     * `incident.submit` is split from `incident.manage` for the same reason
     * `submission.finalize` is split from `submission.answer`: recording "we told
     * the regulator this, at this time" is a statement about a filing to a
     * national authority, and it should be a narrower set of people than those
     * who may type into the draft. At 2am you want everyone technical able to
     * write the draft, and a short list able to assert it was sent.
     */
    'incident.read',
    /** Declare an incident, edit it, write and save report drafts. */
    'incident.manage',
    /** Record that a report was submitted, to whom and when. */
    'incident.submit',
    /**
     * CRA (Regulation (EU) 2024/2847) conformity pack — the per-product
     * artefacts a manufacturer keeps on file: classification, conformity route,
     * SBOM reference, support period, the EU Declaration of Conformity and the
     * coordinated-disclosure / ENISA-reporting record for handled vulnerabilities.
     *
     * WHY THESE ARE NEW RATHER THAN BORROWED
     * The nearest existing capabilities are each the wrong record:
     *   `submission.*` — those gate the `cra` self-ASSESSMENT the organisation
     *                    fills in. A conformity PRODUCT register is a different
     *                    artefact: reusing them would tie "may edit a product's
     *                    CE dossier" to "may answer the questionnaire", and would
     *                    silently hand product management to every assessor.
     *   `incident.*`   — a handled vulnerability under Annex I Part II is a
     *                    product-security record on a manufacturer's own product,
     *                    not a NIS2 Article 23 incident against the organisation.
     *                    They share a reporting shape, not an audience.
     *   `report.read`  — too broad and the wrong axis: it gates document exports
     *                    across the product, not who may maintain a CE dossier.
     *
     * WHY TWO AND NOT ONE
     * Read is split from manage for the same reason the rest of the matrix is:
     * an auditor and an executive must READ a product's CE-readiness and its
     * vulnerability record (it is core conformity evidence) without being able to
     * change a classification, add a vulnerability or alter a support period.
     * The Declaration of Conformity PDF is generated under `cra.read`, like every
     * other tenant document a reader is entitled to produce for their own org.
     */
    'cra.read',
    'cra.manage',
    /**
     * Trust Centre — authoring the pre-answered "reverse replies" and granting
     * EXTERNAL parties gated, read-only access to them.
     *
     * WHY THIS IS NEW RATHER THAN BORROWED
     * `org.update` already gates what the PUBLIC Trust Centre page says, and it
     * stays that way — deciding what the whole internet sees is an administrator's
     * call. But authoring the reverse-reply knowledge base and running the roster
     * of external users is day-to-day compliance work: it is precisely what the
     * SECURITY role exists for, and tying it to `org.update` would have locked
     * SECURITY out of the one feature most squarely in its remit. Splitting it
     * also keeps the grant separately auditable — "may manage who outside the
     * company can read our answers" is a sentence a reviewer should see on its
     * own, not folded into "may change organisation settings".
     */
    'trust.manage',
    // Credits & reporting
    'credit.read',
    'credit.adjust',
    'report.read',
    'report.system', // platform-wide reports
    'audit.read',
    // Self-service
    'profile.read',
    'profile.update',
    'notification.read',
    /**
     * Mark one's OWN notifications read. Separated from `notification.read` so
     * the route declares the write it performs rather than hiding behind a read
     * capability — the fail-closed guard can only be as honest as the
     * declarations.
     *
     * It is in BASE, including for AUDITOR, and that is not a contradiction of
     * "an auditor has no write capability": the only row it touches is the
     * caller's own unread flag, which is a personal reading marker and is part of
     * no compliance record. An auditor who cannot dismiss their own notifications
     * is looking at a permanently red badge, not at better evidence.
     */
    'notification.update',
];
/** Capabilities available to every authenticated user regardless of role. */
const BASE = [
    'profile.read', 'profile.update', 'notification.read', 'notification.update',
];
/**
 * ROLE MATRIX
 *
 * Read this as "what does this job actually need?" — a role should be the
 * smallest set that lets someone do their work. Widening a role is a security
 * decision; make it deliberately.
 */
export const ROLE_CAPABILITIES = {
    /** Full control of their own organisation, including its people. */
    ADMINISTRATOR: [
        ...BASE,
        'org.read', 'org.update', 'org.consent.manage', 'org.group.manage',
        'user.read', 'user.create', 'user.update', 'user.delete', 'user.credentials.reset',
        'framework.read', 'framework.purchase',
        'submission.read', 'submission.answer', 'submission.finalize', 'submission.reopen',
        'evidence.upload', 'evidence.delete',
        'control.evaluate', 'control.custom.manage',
        'gap.read', 'gap.update',
        'plan.read', 'plan.create', 'plan.update', 'plan.delete',
        'risk.read', 'risk.manage',
        'policy.read', 'policy.create', 'policy.update', 'policy.delete', 'policy.approve',
        'template.manage',
        'vendor.read', 'vendor.create', 'vendor.update', 'vendor.delete', 'vendor.assess',
        'scan.read', 'scan.schedule',
        'maritime.read', 'maritime.create', 'maritime.update', 'maritime.delete',
        'incident.read', 'incident.manage', 'incident.submit',
        'cra.read', 'cra.manage',
        'trust.manage',
        'credit.read', 'report.read', 'audit.read',
    ],
    /** Runs the compliance programme. Everything operational, no user admin. */
    SECURITY: [
        ...BASE,
        'org.read',
        'user.read',
        'framework.read',
        'submission.read', 'submission.answer', 'submission.finalize', 'submission.reopen',
        'evidence.upload', 'evidence.delete',
        'control.evaluate', 'control.custom.manage',
        'gap.read', 'gap.update',
        'plan.read', 'plan.create', 'plan.update',
        'risk.read', 'risk.manage',
        'policy.read', 'policy.create', 'policy.update',
        'vendor.read', 'vendor.create', 'vendor.update', 'vendor.assess',
        'scan.read', 'scan.schedule',
        'maritime.read', 'maritime.create', 'maritime.update',
        'incident.read', 'incident.manage', 'incident.submit',
        'cra.read', 'cra.manage',
        'trust.manage',
        'credit.read', 'report.read', 'audit.read',
    ],
    /** Implements controls. Evidence and control status, plus plan progress. */
    TECHNOLOGY: [
        ...BASE,
        'org.read',
        'framework.read',
        'submission.read', 'submission.answer',
        'evidence.upload', 'evidence.delete',
        'control.evaluate', 'control.custom.manage',
        'gap.read', 'gap.update',
        'plan.read', 'plan.update',
        'risk.read',
        'policy.read',
        'vendor.read',
        'scan.read',
        'maritime.read', 'maritime.update',
        // Drafts but does not assert the filing: see the note on `incident.submit`.
        'incident.read', 'incident.manage',
        // Reads the product CE dossier; does not change the classification or
        // conformity route. Technology implements, it does not sign off conformity.
        'cra.read',
        'report.read',
    ],
    /** Oversight and sign-off. Reads everything; approves policies. */
    EXECUTIVE: [
        ...BASE,
        'org.read',
        'user.read',
        'framework.read',
        'submission.read',
        'gap.read',
        'plan.read',
        'risk.read',
        'policy.read', 'policy.approve',
        'vendor.read',
        'scan.read',
        'maritime.read',
        'incident.read',
        'cra.read',
        'credit.read', 'report.read',
    ],
    /**
     * Strictly read-only, including the audit trail. An auditor who can change
     * anything is not an auditor — this role has no write capability at all,
     * deliberately, not even 'profile.update'... except their own profile, which
     * is a practical necessity and changes no compliance record.
     */
    AUDITOR: [
        ...BASE,
        'org.read',
        'user.read',
        'framework.read',
        'submission.read',
        'gap.read',
        'plan.read',
        'risk.read',
        'policy.read',
        'vendor.read',
        'scan.read',
        'maritime.read',
        // Read-only, like everything else this role holds. A handled incident is
        // evidence of the incident-handling control, so an auditor must be able to
        // read the record — and must not be able to add to it.
        'incident.read',
        // Same read-only stance for the CRA conformity dossier: core conformity
        // evidence an auditor must see and must not alter.
        'cra.read',
        'credit.read', 'report.read', 'audit.read',
    ],
    /**
     * Minimal. An employee exists in the system so work can be assigned to them.
     * They see the remediation plans they own and nothing else.
     */
    EMPLOYEE: [
        ...BASE,
        'org.read',
        'plan.readOwn', 'plan.updateOwn',
    ],
};
/**
 * TIER MATRIX
 *
 * Capabilities an organisation's tier ADDS. These are powers over things
 * outside the organisation itself, so they are gated by what the organisation
 * is, not only by who the user is.
 */
export const TIER_CAPABILITIES = {
    /** The platform operator. Also the only tier that may edit framework content. */
    SUPERADMIN: [
        'org.create', 'org.delete', 'org.join',
        'framework.manage',
        'credit.adjust',
        'report.system',
    ],
    /**
     * Sells downstream: may create, fund and close organisations beneath it.
     *
     * `org.delete` is included deliberately. It deactivates rather than destroys,
     * `isAdministeredBy` confines it to this distributor's own subtree, and it is
     * reversible via the reactivate route — so the blast radius is one branch of
     * one channel. Withholding it would mean every churned reseller required
     * Tictac to intervene by hand, making the platform owner a bottleneck inside
     * its partners' own businesses.
     */
    DISTRIBUTOR: ['org.create', 'org.delete', 'org.join', 'credit.adjust', 'report.system'],
    /** Manages a client portfolio. Same powers as a distributor, narrower tree. */
    PARTNER: ['org.create', 'org.delete', 'org.join', 'credit.adjust'],
    /** An end customer. No powers beyond itself. */
    CLIENT: [],
    /**
     * A third party that exists only to answer assessments sent to it.
     * Deliberately empty — and note that vendor questionnaire responses do not
     * go through this path at all: they use a tokenised, unauthenticated route
     * with its own narrow validation.
     */
    VENDOR: [],
};
/**
 * CAPABILITY IMPLICATION
 *
 * Some routes are reachable by two different audiences with different breadth:
 * an EMPLOYEE may act on the remediation plans they own, while SECURITY may act
 * on any plan in the organisation. Such a route declares the NARROW capability
 * (`plan.updateOwn`) so the employee can reach it at all, and then narrows the
 * query for owner-scoped callers.
 *
 * That only works if holding the broad capability also satisfies the narrow
 * one — otherwise the broader role is locked out of its own route, which is
 * exactly the bug this map fixes.
 *
 * The direction matters and must never be inverted: broad implies narrow,
 * never the reverse. `plan.update` grants `plan.updateOwn`; `plan.updateOwn`
 * grants nothing wider.
 */
const IMPLIES = {
    'plan.read': ['plan.readOwn'],
    'plan.update': ['plan.updateOwn'],
    // Managing the CRA conformity pack necessarily includes reading it, so a
    // manage-only grant can still reach the read-gated routes (the register, a
    // product's detail, the Declaration PDF).
    'cra.manage': ['cra.read'],
};
/** Expand a role's capabilities with everything they imply. */
function expand(capabilities) {
    const out = new Set(capabilities);
    for (const capability of capabilities) {
        for (const implied of IMPLIES[capability] ?? [])
            out.add(implied);
    }
    return out;
}
/**
 * The authorisation decision. This is the only function that should ever be
 * consulted; do not compare roles or tiers by hand anywhere else.
 */
export function can(actor, capability) {
    if (expand(ROLE_CAPABILITIES[actor.role] ?? []).has(capability))
        return true;
    // A tier capability still requires a role that could plausibly wield it:
    // only administrators and the security role may act on tier powers. Without
    // this, an EMPLOYEE at a distributor could mint credits.
    const tierEligible = actor.role === 'ADMINISTRATOR' || actor.role === 'SECURITY';
    if (tierEligible && expand(TIER_CAPABILITIES[actor.tier] ?? []).has(capability))
        return true;
    return false;
}
/**
 * Convenience for "may read plans at all", collapsing the own-vs-all split so
 * route handlers can check access first and narrow the query afterwards.
 */
export function canReadAnyPlan(actor) {
    return can(actor, 'plan.read') || can(actor, 'plan.readOwn');
}
/** True when the actor may only see plans they personally own. */
export function isPlanOwnerScoped(actor) {
    return !can(actor, 'plan.read') && can(actor, 'plan.readOwn');
}
/** Everything the actor can do — used to drive UI visibility. */
export function capabilitiesFor(actor) {
    return CAPABILITIES.filter((c) => can(actor, c));
}
//# sourceMappingURL=rbac.js.map